/**
 * availability-fetch — retrieves live per-variant availability + estimated ship
 * times from the Polyform backend, keyed by SKU, and merges them onto a schema.
 *
 * The availability check is a live inventory query, so it was split out of the
 * (otherwise cacheable) ProductSchema endpoint to keep schema fetches fast. The
 * schema no longer carries `variants[].available`; this module supplies it.
 *
 * Contract (per SKU):
 *   { "variants": [
 *       { "sku", "shopifyVariantId",
 *         "available": "instock" | "backorder" | "oos",
 *         "estimatedShipTime": "Ships in 1 - 2 business days (12 in stock)"
 *                              | null } ] }
 *
 * The Shopify adapter composes `estimatedShipTime`; the base availability
 * endpoint carries the parts (quantityAvailable, rangeStart/End/Unit) and no
 * prose. The stock count is appended only when there is stock on hand.
 */

import { localizeEndpoint } from './api-base.js';
import { gidToNumericId } from './schema-fetch.js';

/**
 * Build the concrete availability URL from an endpoint template and a SKU.
 * @param {string} endpointTemplate  e.g. ".../sellables/{sku}/availability"
 * @param {string} sku
 * @returns {string|null}
 */
export function buildAvailabilityUrl(endpointTemplate, sku) {
  if (!endpointTemplate || !sku) return null;
  return endpointTemplate.replace('{sku}', encodeURIComponent(sku));
}

/**
 * The fetch-ready availability URL: built from the template + SKU and
 * dev-localized. Single source so no caller forgets the localize step.
 * @param {string} endpointTemplate
 * @param {string} sku
 * @returns {string|null}
 */
export function availabilityUrlFor(endpointTemplate, sku) {
  return localizeEndpoint(buildAvailabilityUrl(endpointTemplate, sku));
}

/**
 * Fetch the availability payload for a SKU.
 * Throws on network / HTTP / parse failure so callers can degrade gracefully
 * (the picker still functions from the schema alone, without ship times).
 *
 * @param {string} url  concrete availability URL (from buildAvailabilityUrl)
 * @param {AbortSignal} [signal]
 * @returns {Promise<Array<object>>} the availability `variants` array
 */
export async function fetchAvailability(url, signal) {
  const res = await fetch(url, {
    method: 'GET',
    headers: { Accept: 'application/json' },
    mode: 'cors',
    credentials: 'omit',
    // Availability is live inventory — never reuse a stale cached copy.
    cache: 'no-store',
    signal,
  });
  if (!res.ok) {
    throw new Error(`Availability fetch failed: ${res.status}`);
  }
  const raw = await res.json();
  return Array.isArray(raw?.variants) ? raw.variants : [];
}

/**
 * Merge availability entries onto a normalised schema's variants.
 *
 * Each schema variant gains `available` ("instock"|"backorder"|"oos") and
 * `estimatedShipTime` from its counterpart availability entry (matched by SKU,
 * falling back to the numeric Shopify variant id). Variants with no counterpart
 * are left untouched, so their `available` stays undefined and the product-level
 * availability flag governs them instead.
 *
 * @param {object} schema             normalised schema (from normalizeSchema)
 * @param {Array<object>} availability the availability `variants` array
 * @returns {object} a new schema with availability merged onto variants
 */
export function mergeAvailability(schema, availability) {
  if (!schema || !Array.isArray(schema.variants)) return schema;
  const list = Array.isArray(availability) ? availability : [];

  const bySku = new Map();
  const byId = new Map();
  for (const a of list) {
    if (a?.sku) bySku.set(a.sku, a);
    const id = gidToNumericId(a?.shopifyVariantId);
    if (id) byId.set(id, a);
  }

  const variants = schema.variants.map((v) => {
    const match =
      (v.sku != null && bySku.get(v.sku)) ||
      (v.id != null && byId.get(String(v.id))) ||
      null;
    if (!match) return v;
    return {
      ...v,
      available: match.available ?? null,
      estimatedShipTime: match.estimatedShipTime ?? null,
    };
  });

  return { ...schema, variants };
}
